<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetInvoiceHeader',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    invoice: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onReferenceInvoiceSelected = (...args) => {
    const { option, value } = args[0];

    return selectClient({ clientId: option.client.id });
  };
  const selectClient = (...args) => {
    const { clientId } = args[0];
    return $fn.value
      .fetch('get_invoice_party_details', { parameters: { id: clientId } })
      .then((data) => setData('invoice.client', data));
  };
  const selectCompany = (...args) => {
    const { companyId } = args[0];
    return $fn.value
      .fetch('get_invoice_party_details', { parameters: { id: companyId } })
      .then((data) => setData('invoice.company', data));
  };
  const checkInvoiceNumber = (...args) => {
    const invoiceNumber = args[0];

    return $fn.value
      .fetch('check', {
        parameters: {
          kind: 'invoice',
          op: 'exists',
          query: invoiceNumber,
          partner_id: _.get(props.invoice, 'client.id'),
        },
      })
      .then((d) => (duplicateInvoiceNumber.value = d.passed))
      .catch((error) => {
        console.error(error);
        duplicateInvoiceNumber.value = false;
      });
  };

  const messages = ref({
    invoiceTypes: {
      correction: $fn.value.pgettext('InvoiceType', 'Correction'),
      storno: $fn.value.pgettext('InvoiceType', 'Storno'),
      proforma: $fn.value.pgettext('InvoiceType', 'Pro Forma'),
    },
    invoiceTypeHelp: {
      normal: $fn.value.pgettext(
        'Invoice|Reference|description',
        'Normal invoices can reference Pro Forma invoices.'
      ),
      correction: $fn.value.pgettext(
        'Invoice|Reference|description',
        'A correction must reference the invoice being corrected.'
      ),
      storno: $fn.value.pgettext(
        'Invoice|Reference|description',
        'The reversing invoice must reference the invoice being reversed.'
      ),
      proforma: $fn.value.pgettext(
        'Invoice|Reference|description',
        'Pro Forma invoices can reference former Pro Forma invoices.'
      ),
    },
    duplicateInvoiceNumber: $fn.value.pgettext(
      'Invoice',
      'The given invoice number already exists.'
    ),
  });
  const editCompany = ref(false);
  const editClient = ref(false);
  const duplicateInvoiceNumber = ref(false);
  const currencies = ref(null);
  const paymentMethods = ref(null);
  const expenseTypes = ref(null);
  const invoiceStatuses = ref(null);
  const incomingInvoiceStatuses = ref(null);
  const invoiceTypes = ref(null);
  const languages = ref([
    {
      value: 'hu',
      text: $fn.value.pgettext('Language', 'hungarian'),
    },
    {
      value: 'en',
      text: $fn.value.pgettext('Language', 'english'),
    },
  ]);
  const tags = ref(null);
  const vatPeriods = ref(null);
  const ux = ref([]);
  const b_6917646200545441_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'own',
    },
  }));
  const b_6424600534600827_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'partner',
    },
  }));

  const $data = computed(() => ({
    messages: messages.value,
    editCompany: editCompany.value,
    editClient: editClient.value,
    duplicateInvoiceNumber: duplicateInvoiceNumber.value,
    currencies: currencies.value,
    paymentMethods: paymentMethods.value,
    expenseTypes: expenseTypes.value,
    invoiceStatuses: invoiceStatuses.value,
    incomingInvoiceStatuses: incomingInvoiceStatuses.value,
    invoiceTypes: invoiceTypes.value,
    languages: languages.value,
    tags: tags.value,
    vatPeriods: vatPeriods.value,
    ux: ux.value,
    b_6917646200545441_dataSource: b_6917646200545441_dataSource.value,
    b_6424600534600827_dataSource: b_6424600534600827_dataSource.value,
  }));

  const $$load_currencies = () => {
    return $app.value.repo.get('list_currency');
  };
  const $$load_paymentMethods = () => {
    return $app.value.repo.get('list_payment_method').then((data) =>
      _.map(data, (p) => ({
        value: p.id,
        text: $fn.value.pgettext('PaymentMethod', p.name),
      }))
    );
  };
  const $$load_expenseTypes = () => {
    return $app.value.repo.get('list_object_by_type', {
      parameters: { object_type: 'expense-type' },
    });
  };
  const $$load_invoiceStatuses = () => {
    return $fn.value.getEnumValuesFromSchema(
      isIncomingInvoice.value
        ? 'IncomingInvoiceStatus'
        : 'OutgoingInvoiceStatus',
      { sort: false }
    );
  };
  const $$load_incomingInvoiceStatuses = () => {
    return $fn.value.getEnumValuesFromSchema('IncomingInvoiceStatus', {
      sort: false,
    });
  };
  const $$load_invoiceTypes = () => {
    return $fn.value.getEnumValuesFromSchema('InvoiceType', { sort: false });
  };
  const $$load_tags = () => {
    return $app.value.repo
      .get('get_tags', { parameters: { tag_type: 'invoice' } })
      .then((data) => _.map(data, (p) => p.name));
  };
  const $$load_vatPeriods = () => {
    return $app.value.repo.get('list_vat_periods');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages', dependsOn: ['invoiceTypes'] },
      { name: 'editCompany' },
      { name: 'editClient' },
      { name: 'duplicateInvoiceNumber' },
      { name: 'currencies' },
      { name: 'paymentMethods' },
      { name: 'expenseTypes' },
      { name: 'invoiceStatuses' },
      { name: 'incomingInvoiceStatuses' },
      { name: 'invoiceTypes' },
      { name: 'languages' },
      { name: 'tags' },
      { name: 'vatPeriods' },
      { name: 'ux' },
      { name: 'b_6917646200545441_dataSource', nodeId: '6917646200545441' },
      { name: 'b_6424600534600827_dataSource', nodeId: '6424600534600827' },
    ],
    loaders: {
      currencies: $$load_currencies,
      paymentMethods: $$load_paymentMethods,
      expenseTypes: $$load_expenseTypes,
      invoiceStatuses: $$load_invoiceStatuses,
      incomingInvoiceStatuses: $$load_incomingInvoiceStatuses,
      invoiceTypes: $$load_invoiceTypes,
      tags: $$load_tags,
      vatPeriods: $$load_vatPeriods,
    },
    ux: {},
    dataRefs: {
      messages,
      editCompany,
      editClient,
      duplicateInvoiceNumber,
      currencies,
      paymentMethods,
      expenseTypes,
      invoiceStatuses,
      incomingInvoiceStatuses,
      invoiceTypes,
      languages,
      tags,
      vatPeriods,
      ux,
      b_6917646200545441_dataSource,
      b_6424600534600827_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    return false;
  });
  const isIncomingInvoice = computed(() => {
    return props.invoice.invoice_kind == 'incoming';
  });
  const isReferenceInvoiceRequired = computed(() => {
    return ['storno', 'correction'].indexOf(props.invoice.invoice_type) > -1;
  });
  const preventChangeClient = computed(() => {
    return !!props.invoice.reference_invoice;
  });
  const preventChangeCompany = computed(() => {
    return !!props.invoice.reference_invoice;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_4186377094414664_script_ztxz8xqj2_value_callable = async (
    $event
  ) => {
    return onReferenceInvoiceSelected($event.data);
  };
  const act_4186377094414664_script_ztxz8xqj2_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4186377094414664_script_ztxz8xqj2_value_callable($event);
      },
    };
  };
  const act_6917646200545441_script_o1hbbg7tm_value_callable = async (
    $event
  ) => {
    return selectCompany({ companyId: props.invoice.company.id });
  };
  const act_6917646200545441_script_o1hbbg7tm_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6917646200545441_script_o1hbbg7tm_value_callable($event);
      },
    };
  };
  const act_6917646200545441_script_o1hbbg7tm_getWhen = async ($event) => {
    return !!props.invoice.company;
  };
  const act_6424600534600827_script_o10h4b40w_value_callable = async (
    $event
  ) => {
    return selectClient({ clientId: props.invoice.client.id });
  };
  const act_6424600534600827_script_o10h4b40w_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6424600534600827_script_o10h4b40w_value_callable($event);
      },
    };
  };
  const act_6424600534600827_script_o10h4b40w_getWhen = async ($event) => {
    return !!props.invoice.client;
  };
  const act_2754826507016131_script_cpc8ikl7w_value_callable = async (
    $event
  ) => {
    return checkInvoiceNumber(props.invoice.invoice_number);
  };
  const act_2754826507016131_script_cpc8ikl7w_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2754826507016131_script_cpc8ikl7w_value_callable($event);
      },
    };
  };
  const inst_evh_2754826507016131_blur_zbnpdxy3u_getWhen = async ($event) => {
    return props.invoice.isValueChanged('invoice_number');
  };

  const act_7315092382398562_reloadComponentData_rfjxzbl9f =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ayzmkpb2a =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_rfjxzbl9f,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ayzmkpb2a],
    event: 'reload',
  });
  const act_4186377094414664_script_ztxz8xqj2 = new actionTypes.ScriptAction({
    actionArgs: act_4186377094414664_script_ztxz8xqj2_getActionArgs,
  });
  const inst_evh_4186377094414664_optionSelected_qkunhswel =
    new actionTypes.EventHandlerImpl({
      action: act_4186377094414664_script_ztxz8xqj2,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_4186377094414664_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4186377094414664_optionSelected_qkunhswel],
      event: 'optionSelected',
    });
  const act_6917646200545441_script_o1hbbg7tm = new actionTypes.ScriptAction({
    actionArgs: act_6917646200545441_script_o1hbbg7tm_getActionArgs,
    when: act_6917646200545441_script_o1hbbg7tm_getWhen,
  });
  const inst_evh_6917646200545441_selected_ax7brnca1 =
    new actionTypes.EventHandlerImpl({
      action: act_6917646200545441_script_o1hbbg7tm,
      event: 'selected',
      displayName: 'script',
    });
  const inst_evh_6917646200545441_selected = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6917646200545441_selected_ax7brnca1],
    event: 'selected',
  });
  const act_6424600534600827_script_o10h4b40w = new actionTypes.ScriptAction({
    actionArgs: act_6424600534600827_script_o10h4b40w_getActionArgs,
    when: act_6424600534600827_script_o10h4b40w_getWhen,
  });
  const inst_evh_6424600534600827_selected_iywkwbt9a =
    new actionTypes.EventHandlerImpl({
      action: act_6424600534600827_script_o10h4b40w,
      event: 'selected',
      displayName: 'script',
    });
  const inst_evh_6424600534600827_selected = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6424600534600827_selected_iywkwbt9a],
    event: 'selected',
  });
  const act_2754826507016131_script_cpc8ikl7w = new actionTypes.ScriptAction({
    actionArgs: act_2754826507016131_script_cpc8ikl7w_getActionArgs,
  });
  const inst_evh_2754826507016131_blur_zbnpdxy3u =
    new actionTypes.EventHandlerImpl({
      action: act_2754826507016131_script_cpc8ikl7w,
      event: 'blur',
      when: inst_evh_2754826507016131_blur_zbnpdxy3u_getWhen,
      displayName: 'script',
    });
  const inst_evh_2754826507016131_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2754826507016131_blur_zbnpdxy3u],
    event: 'blur',
  });

  const actions = {
    inst_evh_7315092382398562_reload_ayzmkpb2a,
    inst_evh_7315092382398562_reload,
    inst_evh_4186377094414664_optionSelected_qkunhswel,
    inst_evh_4186377094414664_optionSelected,
    inst_evh_6917646200545441_selected_ax7brnca1,
    inst_evh_6917646200545441_selected,
    inst_evh_6424600534600827_selected_iywkwbt9a,
    inst_evh_6424600534600827_selected,
    inst_evh_2754826507016131_blur_zbnpdxy3u,
    inst_evh_2754826507016131_blur,
  };

  const evh_7315092382398562_reload_ayzmkpb2a = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ayzmkpb2a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_optionSelected_qkunhswel = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4186377094414664_optionSelected_qkunhswel.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4186377094414664_optionSelected = async (event, scope) => {
    return await actions.inst_evh_4186377094414664_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6917646200545441_selected_ax7brnca1 = async (event, scope) => {
    return await actions.inst_evh_6917646200545441_selected_ax7brnca1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6917646200545441_selected = async (event, scope) => {
    return await actions.inst_evh_6917646200545441_selected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_selected_iywkwbt9a = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_selected_iywkwbt9a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6424600534600827_selected = async (event, scope) => {
    return await actions.inst_evh_6424600534600827_selected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2754826507016131_blur_zbnpdxy3u = async (event, scope) => {
    return await actions.inst_evh_2754826507016131_blur_zbnpdxy3u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2754826507016131_blur = async (event, scope) => {
    return await actions.inst_evh_2754826507016131_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpFormGroup
      name="status"
      :label="pgettext('Invoice', 'Type')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormNativeSelect
        :autofocus="!isReadonly"
        name="status"
        v-model="invoice.invoice_type"
        :options="invoiceTypes"
        valueField="value"
        textField="text"
        :readonly="isReadonly"
        required
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="reference_invoice"
      :label="pgettext('Invoice', 'Reference Invoice')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      :required="isReferenceInvoiceRequired"
    >
      <template #description>
        <div v-if="invoice.invoice_type">
          {{ messages.invoiceTypeHelp[invoice.invoice_type] }}
        </div>
      </template>
      <ItpFormSearchableSelect
        name="reference_invoice"
        v-model="invoice.reference_invoice"
        :dataSource="
          () => ({
            name: 'suggest_invoice',
            parameters: {
              invoice_kind: invoice.invoice_kind,
              parent_invoice_type: invoice.invoice_type,
              client_id: invoice.client ? invoice.client.id : null,
            },
          })
        "
        valueField="id"
        textField="name"
        bind-object
        :readonly="isReadonly"
        :disabled="!invoice.invoice_type"
        clearable
        use-option-template-for-selected-option
        optionTemplate='<UcSuggestInvoiceOption :option="option" hide-invoice-kind></UcSuggestInvoiceOption>'
        @optionSelected="evh_4186377094414664_optionSelected($event, {})"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <hr class="my-4" />
    <ItpFormGroup
      name="client"
      :label="pgettext('Invoice', 'Own company')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSearchableSelect
        name="company"
        v-model="invoice.company"
        bind-object
        :dataSource="b_6917646200545441_dataSource"
        required
        :disabled="preventChangeCompany"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
        @selected="evh_6917646200545441_selected($event, {})"
      >
      </ItpFormSearchableSelect>
      <template #description>
        <div class="mt-2 p-2 bg-light">
          <div class="d-flex justify-content-end">
            <ItpFormCheckbox name="editCompany" v-model="editCompany" is-switch>
              {{ pgettext('Invoice', 'Toggle editor') }}
            </ItpFormCheckbox>
          </div>
          <UcInvoicePartyPanel
            :item="invoice.company"
            display-name
            v-if="invoice.company && !editCompany"
          >
          </UcInvoicePartyPanel>
          <FieldsetInvoiceParty
            :invoice="invoice"
            path="company"
            v-if="invoice.company && editCompany"
          >
          </FieldsetInvoiceParty>
        </div>
      </template>
    </ItpFormGroup>
    <ItpFormGroup
      name="client"
      :label="gettext('Client')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSearchableSelect
        name="client"
        v-model="invoice.client"
        bind-object
        :dataSource="b_6424600534600827_dataSource"
        required
        :disabled="preventChangeClient"
        optionTemplate="<UcSuggestCompanyOption :option=option></UcSuggestCompanyOption>"
        use-option-template-for-selected-option
        @selected="evh_6424600534600827_selected($event, {})"
      >
      </ItpFormSearchableSelect>
      <template #description>
        <div class="mt-2 p-2 bg-light">
          <div class="d-flex justify-content-end">
            <ItpFormCheckbox name="editClient" v-model="editClient" is-switch>
              {{ pgettext('Invoice', 'Toggle editor') }}
            </ItpFormCheckbox>
          </div>
          <UcInvoicePartyPanel
            :item="invoice.client"
            display-name
            v-if="invoice.client && !editClient"
          >
          </UcInvoicePartyPanel>
          <FieldsetInvoiceParty
            :invoice="invoice"
            path="client"
            v-if="invoice.client && editClient"
          >
          </FieldsetInvoiceParty>
        </div>
      </template>
    </ItpFormGroup>
    <hr class="my-4" />
    <ItpFormGroup
      name="invoice_number"
      :label="gettext('Invoice Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      required
      v-if="isIncomingInvoice"
    >
      <ItpFormInput
        name="invoice_number"
        v-model="invoice.invoice_number"
        :readonly="isReadonly"
        :placeholder="
          pgettext('Invoice/invoice_number/placeholder', 'Enter invoice number')
        "
        required
        @blur="evh_2754826507016131_blur($event, {})"
      >
      </ItpFormInput>
      <div
        class="alert alert-in-form alert-warning"
        v-if="duplicateInvoiceNumber"
      >
        <ItpIcon fa="exclamation-circle"> </ItpIcon>
        <span>
          {{ messages.duplicateInvoiceNumber }}
        </span>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      name="invoice_date"
      :label="gettext('Invoice Date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormDatetime
        name="invoice_date"
        v-model="invoice.invoice_date"
        :readonly="isReadonly"
        required
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="fulfillment_date"
      :label="gettext('Fulfillment Date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormDatetime
        name="fulfillment_date"
        v-model="invoice.fulfillment_date"
        :readonly="isReadonly"
        required
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="due_date"
      :label="pgettext('Invoice', 'Due Date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormDatetime
        name="due_date"
        v-model="invoice.due_date"
        :readonly="isReadonly"
        required
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="payment_method"
      :label="pgettext('Invoice', 'Payment Method')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      required
      class="col"
    >
      <ItpFormNativeSelect
        name="payment_method"
        v-model="invoice.payment_method"
        :options="paymentMethods"
        :readonly="isReadonly"
        required
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <hr class="my-4" />
    <ItpFormGroup
      name="status"
      :label="pgettext('Invoice', 'Status')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      required
      v-if="isIncomingInvoice"
    >
      <ItpFormNativeSelect
        name="status"
        v-model="invoice.status"
        :options="incomingInvoiceStatuses"
        valueField="value"
        textField="text"
        :readonly="isReadonly"
        required
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="accounting_date"
      :label="pgettext('Invoice', 'Accounting Date')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
    >
      <ItpFormDatetime
        name="accounting_date"
        v-model="invoice.accounting_date"
        :readonly="isReadonly"
      >
      </ItpFormDatetime>
    </ItpFormGroup>
    <ItpFormGroup
      name="billing_period"
      :label="gettext('Billing Period')"
      contentClass="row"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      v-if="invoice.billing_period"
    >
      <div class="col-4">
        <ItpFormDatetime
          name="billing_period_start"
          v-model="invoice.billing_period.start"
          :readonly="isReadonly"
          :placeholder="pgettext('BillingPeriod', 'From')"
        >
        </ItpFormDatetime>
      </div>
      <div class="col-4">
        <ItpFormDatetime
          name="billing_period_end"
          v-model="invoice.billing_period.end"
          :readonly="isReadonly"
          :placeholder="pgettext('BillingPeriod', 'To')"
        >
        </ItpFormDatetime>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      name="currency"
      :label="pgettext('Invoice', 'Currency')"
      :label-cols-md="4"
      :content-cols-md="2"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormNativeSelect
        name="currency"
        v-model="invoice.currency"
        :options="currencies"
        valueField="code"
        textField="code"
        :readonly="isReadonly"
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="currency"
      :label="pgettext('Invoice', 'Exchange rate')"
      contentClass="row"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      class="col"
    >
      <div class="col-3">
        <ItpFormInput
          type="number"
          name="exchange_rate"
          v-model="invoice.exchange_rate"
          :readonly="isReadonly"
          :placeholder="pgettext('Invoice', 'Rate')"
        >
        </ItpFormInput>
      </div>
      <div class="col-4">
        <ItpFormDatetime
          name="exchange_rate_date"
          v-model="invoice.exchange_rate_date"
          :readonly="isReadonly"
          :placeholder="pgettext('Invoice', 'Date')"
        >
        </ItpFormDatetime>
      </div>
      <div class="col-5">
        <ItpFormInput
          name="exchange_rate_bank"
          v-model="invoice.exchange_rate_bank"
          :readonly="isReadonly"
          :placeholder="pgettext('Invoice', 'Provider')"
        >
        </ItpFormInput>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      name="language"
      :label="gettext('Language')"
      :label-cols-md="4"
      :content-cols-md="4"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormNativeSelect
        name="language"
        v-model="invoice.language"
        :options="languages"
        :readonly="isReadonly"
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      name="expense_type"
      :label="gettext('Expense Type')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormNativeSelect
        name="expense_type"
        v-model="invoice.expense_type"
        :options="expenseTypes"
        :readonly="isReadonly"
        valueField="name"
        textField="name"
      >
      </ItpFormNativeSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="tags"
      :label="pgettext('Invoice', 'Categories')"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormSearchableSelect
        name="tags"
        multiple
        taggable
        :options="tags"
        v-model="invoice.tags"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="tags"
      :label="pgettext('Invoice', 'VAT Period')"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormSearchableSelect
        name="vat_period"
        v-model="invoice.vat_period"
        bind-object
        :options="vatPeriods"
        valueField="id"
        textField="name"
      >
      </ItpFormSearchableSelect>
    </ItpFormGroup>
    <hr class="my-4" />
    <ItpFormGroup
      name="reference_number"
      :label="pgettext('Invoice', 'Reference Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormInput
        name="reference_number"
        v-model="invoice.reference_number"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="order_number"
      :label="pgettext('Invoice', 'Order Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormInput
        name="order_number"
        v-model="invoice.order_number"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      name="file_number"
      :label="pgettext('Invoice', 'File Number')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormInput
        name="file_number"
        v-model="invoice.file_number"
        :readonly="isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <hr class="my-4" />
    <ItpFormGroup
      name="notes"
      :label="pgettext('Invoice', 'Notes')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormTextarea
        name="notes"
        v-model="invoice.notes"
        :readonly="isReadonly"
      >
      </ItpFormTextarea>
    </ItpFormGroup>
    <ItpFormGroup
      name="internal_notes"
      :label="pgettext('Invoice', 'Internal Notes')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
      class="col"
    >
      <ItpFormTextarea
        name="internal_notes"
        v-model="invoice.internal_notes"
        :readonly="isReadonly"
        :rows="4"
      >
      </ItpFormTextarea>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
